import { create } from "zustand";
import { API } from "@/lib/apiConfig";

/**
 * EU Anti-Fraud Interoperability Layer Store (v3)
 * 
 * Manages risk scoring, PEPPOL/eSeal verification, UBO graph analysis,
 * evidence attachment, and interop reporting.
 */
export interface AntiFraudStoreState {
  // Risk Scoring
  riskScore: number;
  riskBand: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  riskTx: {
    buyer: string;
    supplier: string;
    amount: number;
    currency: string;
    country: string;
    lotId?: string;
    channel: "PEPPOL" | "EMAIL" | "PORTAL" | "OTHER";
    meta?: Record<string, any>;
  } | null;

  // PEPPOL/eSeal Verification
  peppolStatus: "VALID" | "UNKNOWN";
  peppolSealValid: boolean;
  peppolOk: boolean;
  verification: {
    docId: string;
    participantId: string;
    sealHash?: string;
  } | null;

  // UBO Graph
  uboGraph: {
    companyId: string;
    graph: Array<{
      source: string;
      target: string;
      relation: "owns" | "controls" | "director" | "family" | "political";
    }>;
  } | null;
  uboHints: string[];
  uboEdgesCount: number;

  // Evidence Attachment
  evidenceAttachments: Array<{
    id: string;
    ref: string;
    attachments: Array<{ hash?: string; url?: string; kind?: string }>;
  }>;

  // Interop Report
  interopReports: Array<{
    ref: string;
    kind: string;
    hashes?: string[];
    accepted: boolean;
  }>;

  // Audit Log (last N entries)
  auditLog: Array<{
    id: string;
    ts: number;
    type: string;
    [key: string]: any;
  }>;

  // Actions
  computeRiskScore: (tx: AntiFraudStoreState["riskTx"]) => Promise<void>;
  verifyPeppol: (docId: string, participantId: string, sealHash?: string) => Promise<void>;
  submitUboGraph: (companyId: string, graph: AntiFraudStoreState["uboGraph"]["graph"]) => Promise<void>;
  attachEvidence: (ref: string, attachments: Array<{ hash?: string; url?: string; kind?: string }>) => Promise<void>;
  reportInterop: (ref: string, kind: string, hashes?: string[]) => Promise<void>;
  fetchAuditLog: () => Promise<void>;

  // API Base URL
  apiBaseUrl: string;
  setApiBaseUrl: (url: string) => void;
}

const API_BASE = API.antiFraud;

export const useAntiFraudStore = create<AntiFraudStoreState>((set, get) => ({
  riskScore: 0,
  riskBand: "LOW",
  riskTx: null,

  peppolStatus: "UNKNOWN",
  peppolSealValid: false,
  peppolOk: false,
  verification: null,

  uboGraph: null,
  uboHints: [],
  uboEdgesCount: 0,

  evidenceAttachments: [],
  interopReports: [],
  auditLog: [],

  apiBaseUrl: API_BASE,
  setApiBaseUrl: (url) => set({ apiBaseUrl: url }),

  computeRiskScore: async (tx) => {
    if (!tx) return;
    try {
      const response = await fetch(`${get().apiBaseUrl}/risk/score`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || "dev-token"}`,
          "x-api-key": localStorage.getItem("apiKey") || "",
        },
        body: JSON.stringify(tx),
      });
      const data = await response.json();
      if (response.ok) {
        set({
          riskScore: data.score || 0,
          riskBand: data.band || "LOW",
          riskTx: tx,
        });
      }
    } catch (error) {
      console.error("Risk score computation failed:", error);
    }
  },

  verifyPeppol: async (docId, participantId, sealHash) => {
    try {
      const response = await fetch(`${get().apiBaseUrl}/peppol/verify`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || "dev-token"}`,
          "x-api-key": localStorage.getItem("apiKey") || "",
        },
        body: JSON.stringify({ docId, participantId, sealHash }),
      });
      const data = await response.json();
      if (response.ok) {
        set({
          peppolStatus: data.status || "UNKNOWN",
          peppolSealValid: data.sealValid || false,
          peppolOk: data.peppolOk || false,
          verification: { docId, participantId, sealHash },
        });
      }
    } catch (error) {
      console.error("PEPPOL verification failed:", error);
    }
  },

  submitUboGraph: async (companyId, graph) => {
    try {
      const response = await fetch(`${get().apiBaseUrl}/graph/ubo`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || "dev-token"}`,
          "x-api-key": localStorage.getItem("apiKey") || "",
        },
        body: JSON.stringify({ companyId, graph }),
      });
      const data = await response.json();
      if (response.ok) {
        set({
          uboGraph: { companyId, graph },
          uboHints: data.hints || [],
          uboEdgesCount: data.edges || 0,
        });
      }
    } catch (error) {
      console.error("UBO graph submission failed:", error);
    }
  },

  attachEvidence: async (ref, attachments) => {
    try {
      const response = await fetch(`${get().apiBaseUrl}/evidence/attach`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || "dev-token"}`,
          "x-api-key": localStorage.getItem("apiKey") || "",
        },
        body: JSON.stringify({ ref, attachments }),
      });
      const data = await response.json();
      if (response.ok && data.id) {
        set((state) => ({
          evidenceAttachments: [
            ...state.evidenceAttachments,
            { id: data.id, ref, attachments },
          ],
        }));
      }
    } catch (error) {
      console.error("Evidence attachment failed:", error);
    }
  },

  reportInterop: async (ref, kind, hashes) => {
    try {
      const response = await fetch(`${get().apiBaseUrl}/interop/report`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken") || "dev-token"}`,
          "x-api-key": localStorage.getItem("apiKey") || "",
        },
        body: JSON.stringify({ ref, kind, hashes }),
      });
      const data = await response.json();
      if (response.ok) {
        set((state) => ({
          interopReports: [
            ...state.interopReports,
            { ref, kind, hashes, accepted: data.accepted || false },
          ],
        }));
      }
    } catch (error) {
      console.error("Interop report failed:", error);
    }
  },

  fetchAuditLog: async () => {
    try {
      const response = await fetch(`${get().apiBaseUrl}/audit`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken") || "dev-token"}`,
          "x-api-key": localStorage.getItem("apiKey") || "",
        },
      });
      const data = await response.json();
      if (response.ok && data.audit) {
        set({ auditLog: data.audit.slice(-100) }); // Last 100 entries
      }
    } catch (error) {
      console.error("Audit log fetch failed:", error);
    }
  },
}));








